/**
 * User: Rob Richard
 * Date: 5/6/14
 * Time: 12:20 PM
 */

"use strict";

var $ = require('dibs-jquery-shim');
var _ = require('underscore');
var endpoints = require('dibs-endpoints');
var FinancialAmount = require('bunsen/models/FinancialAmount');
var Transaction = require('bunsen/models/ecomm/Transaction');
var TransactionShipmentQuote = require('bunsen/models/ecomm/TransactionShipmentQuote');
var deferredHelpers = require('bunsen/helpers/deferredHelpers');
var getFromInvoice;
var getPriceAmount;
var getFromDealerInvoice;
var getFromBuyerInvoice;
var hasAmount;

require('bunsen/libraries/currency.js');

getPriceAmount = function (type, currency) {
    var price = this.getPriceType(type);
    return price && Number(price.get('amount').getAmountIn(currency));
};

getFromInvoice = function (invoiceAccessor, fn, currency) {
    var invoice = this[invoiceAccessor]();
    if (invoice) {
        return invoice[fn](currency);
    }
};

hasAmount = function (fn) {
    var amount = this[fn](FinancialAmount.CURRENCY_USD);
    return $.isNumeric(amount) && Number(amount) !== 0;
};

getFromDealerInvoice = _.partial(getFromInvoice, 'getDealerInvoice');
getFromBuyerInvoice = _.partial(getFromInvoice, 'getBuyerInvoice');

module.exports = {
    getShippingSubtotal : function (currency) {
        var quote = this.getCurrentShippingQuote();
        if (!quote) {
            return 0;
        }
        return quote.getSubTotal(currency) || 0;
    },
    getShippingTotal: function (currency) {
        var quote = this.getCurrentShippingQuote();
        if (!quote) {
            return null;
        }
        return quote.getTotalAmount(currency) || 0;
    },
    getShippingTaxAmount : function (currency) {
        var buyerInvoice = this.getBuyerInvoice();
        var invoiceItems = buyerInvoice && buyerInvoice.get('invoiceItems');
        if (invoiceItems) {
            return invoiceItems.getShipmentSalesTax(currency);
        }
        return 0;
    },
    getAvailablePrices: function () {
        return this.get('summary.availablePrices');
    },
    getCheckoutPriceAmount: function (currency) {
        var itemPriceAmount = this.get('summary.itemPrice.amount');
        return itemPriceAmount && itemPriceAmount.getAmountIn(currency);
    },
    getCheckoutPriceType: function () {
        return this.get('summary.itemPrice.type');
    },
    getPriceType: function (type) {
        var availablePrices = this.getAvailablePrices();
        return availablePrices && availablePrices.find(function (price) {
            return price.get('type') === type;
        });
    },
    isOnSale: function () {
        return !!this.getPriceType('WEEKLY_SALE');
    },
    hasNetPrice: function () {
        return !!this.getPriceType('NET');
    },
    getRetailAmount: _.partial(getPriceAmount, 'RETAIL'),
    getSaleAmount: _.partial(getPriceAmount, 'WEEKLY_SALE'),
    getNetPriceAmount: _.partial(getPriceAmount, 'NET'),
    getOfferSubtotal : function (currency) {
        var offer = this.getMostRecentOffer();
        if (!offer) {
            return 0;
        }
        return offer.getSubTotal(currency);
    },
    getOfferTotal: function (currency, offer) {
        offer = offer || this.getMostRecentOffer();
        if (!offer) {
            return 0;
        }
        return offer.getTotalAmount(currency);
    },
    getOfferTaxAmount: function (currency) {
        var buyerInvoice = this.getBuyerInvoice();
        var invoiceItems = buyerInvoice && buyerInvoice.get('invoiceItems');
        if (invoiceItems) {
            return invoiceItems.getOfferSalesTax(currency);
        }
        return 0;
    },
    getAdjustmentTaxAmount: function (currency) {
        var buyerInvoice = this.getBuyerInvoice();
        var invoiceItems = buyerInvoice && buyerInvoice.get('invoiceItems');
        if (invoiceItems) {
            return invoiceItems.getAdjustmentSalesTax(currency);
        }
        return 0;
    },
    hasSalesTax: _.partial(hasAmount, 'getOfferTaxAmount'),
    hasShippingSalesTax: _.partial(hasAmount, 'getShippingTaxAmount'),
    getSalesTax : function (currency) {
        return this.getShippingTaxAmount(currency) +
            this.getOfferTaxAmount(currency) +
            this.getAdjustmentTaxAmount(currency);
    },
    getSalesTaxState: function () {
        if (this.isLocalPickup()) {
            return this.get('item.seller.shippingAddress.state');
        } else {
            return this.get('shipment.shippingAddress.state');
        }
    },
    getBuyerTotalFromOffers : function (currency, passedOffer) {
        var offer = passedOffer || this.getMostRecentOffer();
        var offerTotal = this.getOfferTotal(currency, offer) || 0;
        var quote = this.getCurrentShippingQuote();

        // Only include the shipping total when it is not free shipping for the buyer.
        if (quote && quote.get('complimentaryShipping') !== 'Y') {
            return offerTotal + this.getShippingTotal(currency);
        }
        return offerTotal;
    },
    getBuyerTotal: _.partial(getFromBuyerInvoice, 'getTotalAmount'),
    getItemCurrency: function () {
        return this.get('item.pricing.totalAmount.currency');
    },

    getAmountPerItem: function (currency) {
        var item = this.get('item');
        if (item) {
            return item.getAmountPerItem(currency);
        }
    },
    getCommission: _.partial(getFromDealerInvoice, 'calculateCommission'),
    getProcessingFee: _.partial(getFromDealerInvoice, 'calculateProcessingFee'),
    getListingCredit: _.partial(getFromDealerInvoice, 'calculateListingCredit'),
    getComplimentaryShippingAdjustment: _.partial(getFromDealerInvoice, 'calculateComplimentaryShipping'),
    getDealerTotal: _.partial(getFromDealerInvoice, 'getTotalAmount'),
    getDealerSubTotal: _.partial(getFromDealerInvoice, 'getSubTotal'),
    getPromotionAmount: _.partial(getFromBuyerInvoice, 'calculatePromotion'),
    getItemPriceModification: _.partial(getFromBuyerInvoice, 'calculateItemPriceModification'),
    getProductCustomization: _.partial(getFromBuyerInvoice, 'calculateProductCustomization'),
    getCustomerCharge: _.partial(getFromBuyerInvoice, 'calculateCustomerCharge'),
    getDealerCharge: _.partial(getFromDealerInvoice, 'calculateDealerCharge'),
    hasListingCredit: _.partial(hasAmount, 'getListingCredit'),
    hasComplimentaryShippingAdjustment: _.partial(hasAmount, 'getComplimentaryShippingAdjustment'),
    hasPromotion: function () {
        var adjustments = this.getBuyerInvoice() && this.getBuyerInvoice().get('adjustments');
        return adjustments && adjustments.hasPromotion();
    },
    hasItemPriceModification: _.partial(hasAmount, 'getItemPriceModification'),
    hasProductCustomization: _.partial(hasAmount, 'getProductCustomization'),
    hasCustomerCharge: _.partial(hasAmount, 'getCustomerCharge'),
    hasDealerCharge: _.partial(hasAmount, 'getDealerCharge'),
    hasBuyerOrderMod: function () {
        return this.hasItemPriceModification() || this.hasProductCustomization() || this.hasCustomerCharge();
    },
    hasDealerOrderMod: function () {
        return this.hasDealerCharge();
    },
    getBuyerBalanceDueAmount: function (currency) {
        var balanceDueAmount = this.get('summary.balanceDueAmount');
        return balanceDueAmount && balanceDueAmount.getAmountIn(currency) || 0;
    },
    getFinalCaptureAmount: function (currency) {

        var rawAmountToCapture = this.getBuyerTotal(currency);
        var paymentAuthOnShipping = this.getShippingPaymentAuthorization();
        if (paymentAuthOnShipping) {
            rawAmountToCapture -= paymentAuthOnShipping.get('amount').getAmountIn(currency);
        }
        return rawAmountToCapture;
    },
    getMostRecentOfferAmountPerItem: function (currency) {
        var offer = this.getMostRecentOffer();
        if (offer) {
            return offer.getAmountPerItem(currency);
        }
    },
    getAcceptedQuoteSubTotal: function (currency) {
        var sq = this.getAcceptedQuote();
        if (sq) {
            return sq.getSubTotal(currency);
        }
    },
    getOfferPaymentAuthorizationAmount: function (currency) {
        var pa = this.get('paymentAuthorizations').getOfferPaymentAuthorization();
        if (pa) {
            return pa.getAmount(currency);
        }
    },
    getOfferPaymentAuthorizationCapturedAmount: function (currency) {
        var pa = this.get('paymentAuthorizations').getOfferPaymentAuthorization();
        if (pa) {
            return pa.getCapturedAmount(currency);
        }
    },
    getOfferPaymentAuthorizationAuthorizationAmount: function (currency) {
        var pa = this.get('paymentAuthorizations').getOfferPaymentAuthorization();
        if (pa) {
            return pa.getAuthorizationAmount(currency);
        }
    },
    getShippingPaymentAuthorizationAmount: function (currency) {
        var pa = this.get('paymentAuthorizations').getShippingPaymentAuthorization();
        if (pa) {
            return pa.getAmount(currency);
        }
    },
    getBuyerCurrency: function () {
        return this.get('buyer.profile.currency') || FinancialAmount.CURRENCY_USD;
    },
    getBuyerOriginalPayment: function (currency) {
        var offer = this.getMostRecentOffer();
        var offerAmount = offer && offer.getTotalAmount(currency);
        var promotionAmount = this.getPromotionAmount(currency) || 0;
        return offerAmount + promotionAmount;
    },

    hasCurrencyConversionFor: function (currency) {
        var rates = this.get('currencyConversionRateEvent.conversionRates');
        var itemCurrency = this.getItemCurrency();

        return rates &&
            !!rates.findWhere({
                sourceCurrency: FinancialAmount.CURRENCY_USD,
                targetCurrency: currency
            }) &&
            !!rates.findWhere({
                sourceCurrency: itemCurrency,
                targetCurrency: currency
            });
    },

    hasBuyerCurrencyConversion: function () {
        return this.hasCurrencyConversionFor(this.getBuyerCurrency());
    },

    getBuyerLocalizedCurrency: function () {
        var itemHasSameCurrencyAsBuyer = this.getItemCurrency() === this.getBuyerCurrency();
        return itemHasSameCurrencyAsBuyer || this.hasBuyerCurrencyConversion() ? this.getBuyerCurrency() : FinancialAmount.CURRENCY_USD;
    },

    /**
     * @deprecated
     * @returns {*}
     */
    calculateTotals: function () {
        return this.computeAdjustments.apply(this, arguments);
    },
    computeCheckoutAdjustments: function () {
        // we need to preserve paymentAuth payment method since
        // ecom does not get the credit card info
        var paymentAuths = this.get('paymentAuthorizations').toJSON();
        var buyer = this.get('buyer').toJSON();
        var jqXhr;

        // cancel any outstanding calls
        if (this._checkoutAdjustmentsDeferred) {
            this._checkoutAdjustmentsDeferred.reject();
        }

        // compute adjustments starts
        this.trigger('computingAdjustments');

        jqXhr = this.save(null, {url: endpoints.computeAdjustments(), type: 'post'});

        this._checkoutAdjustmentsDeferred = deferredHelpers.wrapDeferred(jqXhr);
        this._checkoutAdjustmentsDeferred.then(_.bind(function () {
            _.each(paymentAuths, function (paymentAuth, i) {
                this.get('paymentAuthorizations[' + i + ']').set('paymentMethod', paymentAuth.paymentMethod, {computeAdjustments: true});
            }, this);
            // need to preserve the buyer or backbone will overwrite it if there's no id
            this.set('buyer', buyer);
            // compute adjustments ends
            this.trigger('computedAdjustments');
        }, this));

        return this._checkoutAdjustmentsDeferred.promise();
    },
    isAdjusted: function () {
        var offer = this.getFirstOffer();
        return offer && offer.has('adjustments.signature');
    },
    removeAdjustments: function () {
        this.get('offers').each(function (offer) {
            offer.unset('adjustments');
        });
        this.get('shipmentQuotes').each(function (shipmentQuote) {
            shipmentQuote.unset('adjustments');
        });
        this.trigger('removedAdjustments');
    },
    setEachModel: function (type, transaction) {
        var collection = this.get(type);
        if (collection) {
            collection.each(function (model, i) {
                if (i < transaction.get(type).length) {
                    model.set(transaction.get(type).at(i).toJSON());
                }
            });
            transaction.get(type).each(function (model) {
                if (!collection.findWhere({ id: model.id })) {
                    collection.push(model);
                }
            });
        }
    },
    setInvoiceModel: function (transaction) {
        var collection = this.get('invoices');
        var model;
        var invoices;
        if (collection) {
            invoices = transaction.get('invoices');
            invoices.each(function (invoice) {
                if (invoice.isBuyerInvoice()) {
                    model = collection.getBuyerInvoice();
                } else {
                    model = collection.getDealerInvoice();
                }

                if (model) {
                    model.set(invoice.toJSON());
                } else {
                    collection.push(invoice);
                }
            });
        }
    },
    _defaultZero: function (model, field) {
        var financialAmount = model.get(field);
        var currency;
        if (financialAmount && !financialAmount.get('amount')) {
            currency = financialAmount.get('currency');
            financialAmount.set({
                amount: 0,
                currency: currency
            });
        }
    },

    /**
     * @memberOf Transaction#
     * @param [shipmentQuote] - update totals/adjustments on this shipmentQuote
     * @return {$.Deferred.promise}
     */
    computeAdjustments: function (shipmentQuote) {
        var transaction = new Transaction(this.toJSON());
        var self = this;
        var newShipmentQuote;
        var transactionShipmentQuote;

        if (shipmentQuote && shipmentQuote.toJSON()) {
            transactionShipmentQuote = new TransactionShipmentQuote(shipmentQuote.toJSON());
        }

        newShipmentQuote = transactionShipmentQuote || transaction.get('shipment.acceptedQuote');
        if (newShipmentQuote) {
            transaction.get('shipment').set('acceptedQuote', newShipmentQuote);
            this._defaultZero(newShipmentQuote, 'subTotalAmount');
            this._defaultZero(newShipmentQuote, 'totalAmount');
        }
        this._defaultZero(transaction.get('offers').last(), 'subTotalAmount');
        this._defaultZero(transaction.get('offers').last(), 'amountPerItem');
        return transaction.save(null, {url: endpoints.computeAdjustments(), type: 'post'}).then(function () {
            var rateEvent;
            /*
             * IMPORTANT
             * form view holds on to sub-model references. we need to actually set each submodel or else
             * they will be replaced with a new model and the views will not have references to them
             */
            _.each(['offers', 'paymentAuthorizations'], function (type) {
                self.setEachModel(type, transaction);
            });
            /*
             * Had to break out 'invoices' from above call to "setEachModel" since invoice is
             * implementation dependent
             */
            self.setInvoiceModel(transaction);
            rateEvent = transaction.get('currencyConversionRateEvent');
            if (rateEvent) {
                self.set('currencyConversionRateEvent', rateEvent.toJSON());
            }
            if (shipmentQuote) {
                self.setAttributeOnShipmentQuote(shipmentQuote, transaction, 'shipment.acceptedQuote.totalAmount');
                self.setAttributeOnShipmentQuote(shipmentQuote, transaction, 'shipment.acceptedQuote.adjustments');
            }
        });
    },

    setAttributeOnShipmentQuote: function (on, from, what) {
        if (!on) {
            return;
        }
        if (what && from.get(what)) {
            on.set(_.last(what.split('.')), from.get(what));
        }
        return this; // eslint-disable-line
    }
};



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/models/mixins/ecomm/Transaction/transactionCurrencyMixin.js
// module id = 194
// module chunks = 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31 32 33 34 35 36 37 38 39 40 41 42 43 44 45 46 47 48 49 50 51 52 53 54